import { useTranslation } from 'react-i18next';
import {
  ApiErrorText,
  CurrencyAmountForm,
  InfoCard,
  Loader,
  ResultContent,
  ServiceFeeDisplay,
} from '@/shared/components';
import { type DepositCardFormValues } from '../model';
import { useCurrencyAmountFormConfig } from '@/shared/lib/formConfigFactory';
import type { OperationDetailsResponse } from '@/shared/types/operationTypes';
import type { UseFormReturn } from 'react-hook-form';
import type {
  DepositCommissionData,
  DepositCurrencyOption,
} from '@/shared/types';

interface DepositCardFormProps {
  form: UseFormReturn<DepositCardFormValues>;
  handleFormSubmit: (data: DepositCardFormValues) => void;
  onCancel?: () => void;
  className?: string;
  currencyOptions: DepositCurrencyOption[];
  minAmount: number;
  amountPlaceholder: string;
  commissionData: DepositCommissionData | undefined;
  shouldShowCommission: boolean;
  isSubmitInProgressWithoutErrors: boolean;
  formError: unknown;
  showResultModal: boolean;
  isSuccess: boolean;
  isCheckingStatus: boolean;
  operationDetails: OperationDetailsResponse | undefined;
  isLoading: boolean;
  isAccountInfoError: boolean;
  accountsInfoError: string | null;
  hasDataError: boolean;
  firstError: string | null;
  resultModalClick: () => void;
  bottomSheetText?: string;
}

export function DepositCardForm({
  form,
  handleFormSubmit,
  onCancel,
  currencyOptions,
  minAmount,
  amountPlaceholder,
  commissionData,
  shouldShowCommission,
  isSubmitInProgressWithoutErrors,
  formError,
  showResultModal,
  isSuccess,
  isCheckingStatus,
  operationDetails,
  isLoading,
  isAccountInfoError,
  accountsInfoError,
  hasDataError,
  firstError,
  resultModalClick,
  className,
  bottomSheetText,
}: DepositCardFormProps) {
  const { t } = useTranslation();

  const formConfig = useCurrencyAmountFormConfig({
    currencyOptions,
    minAmount,
    currencyLabelKey: 'depositMethods.bankTransfer.placeholders.currency',
    amountLabelKey: 'common.amount',
    amountPlaceholderKey: amountPlaceholder,
    cancelButtonKey: 'common.cancel',
    showCancelButton: !!onCancel,
    customContentAfterFields:
      shouldShowCommission && commissionData ? (
        <ServiceFeeDisplay
          commissionData={commissionData}
          className="mt-[-1.2rem]"
        />
      ) : undefined,
    customContentBeforeSubmit: bottomSheetText ? (
      <InfoCard title={t('depositMethods.creditCard.importantInfo.title')}>
        {bottomSheetText}
      </InfoCard>
    ) : undefined,
    className,
  });

  if (showResultModal) {
    if (isCheckingStatus) {
      return <Loader />;
    }

    const operationSuccess =
      operationDetails?.status?.paramStatus === 'done' || isSuccess;

    const resultTitle = t(
      operationSuccess
        ? 'depositMethods.creditCard.result.successTitle'
        : 'depositMethods.creditCard.result.errorTitle'
    );
    const resultDescription = t(
      operationSuccess
        ? 'depositMethods.creditCard.result.successDescription'
        : 'depositMethods.creditCard.result.errorDescription'
    );
    const resultButtonText = t(
      operationSuccess
        ? 'depositMethods.creditCard.result.successButton'
        : 'depositMethods.creditCard.result.errorButton'
    );

    return (
      <ResultContent
        className="w-full"
        isSuccess={operationSuccess}
        title={resultTitle}
        description={resultDescription}
        buttonText={resultButtonText}
        onClick={resultModalClick}
      />
    );
  }

  if (hasDataError) {
    return <ApiErrorText error={firstError} />;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-[4rem]">
        <Loader />
      </div>
    );
  }

  if (isAccountInfoError) {
    return <ApiErrorText error={accountsInfoError} />;
  }

  return (
    <CurrencyAmountForm
      config={formConfig}
      form={form}
      onSubmit={handleFormSubmit}
      isLoading={isSubmitInProgressWithoutErrors}
      error={formError}
    />
  );
}
